// Banner de Modo Crisis. Aparece arriba de TODAS las pantallas cuando hay una
// situación grave abierta, y desaparece solo cuando se cierra.
//
// Es deliberadamente imposible de ignorar: mientras el municipio esté
// clausurando locales, no tiene sentido que la plataforma muestre el día como
// si nada. Se puede plegar a una línea fina, pero no cerrar del todo.
//
// Si la API falla, no renderiza nada: un banner roto no puede romper la app.

import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';
import { FaExclamationTriangle, FaChevronDown, FaChevronUp, FaFolderOpen } from 'react-icons/fa';
import { cambiarEstadoCrisis } from '../../services/crisisService';
import { useCrisis } from '../../contexts/CrisisContext';

const latido = keyframes`
  0%, 100% { opacity: 1; transform: scale(1); }
  50%      { opacity: 0.45; transform: scale(0.82); }
`;

const Barra = styled.div`
  border: 1px solid rgba(239, 71, 111, 0.45);
  border-left: 3px solid var(--alert-error);
  background: linear-gradient(90deg, rgba(239, 71, 111, 0.14), rgba(239, 71, 111, 0.05));
  border-radius: 6px;
  margin: 0 0 20px;
  overflow: hidden;
`;

const Cabecera = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  background: none;
  border: none;
  cursor: pointer;
  text-align: left;
  color: var(--text-primary);
  font-family: inherit;
`;

const Punto = styled.span`
  width: 9px;
  height: 9px;
  border-radius: 50%;
  background: var(--alert-error);
  flex-shrink: 0;
  animation: ${latido} 1.6s ease-in-out infinite;
`;

const Etiqueta = styled.span`
  font-family: 'JetBrains Mono', 'Fira Code', Consolas, monospace;
  font-size: 0.62rem;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: var(--alert-error);
  border: 1px solid rgba(239, 71, 111, 0.4);
  background: rgba(239, 71, 111, 0.08);
  padding: 2px 7px;
  border-radius: 3px;
  flex-shrink: 0;
`;

const Titulo = styled.span`
  flex: 1;
  font-weight: 600;
  font-size: 0.92rem;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const Flecha = styled.span`
  color: var(--text-secondary);
  display: flex;
  flex-shrink: 0;
`;

const Cuerpo = styled.div`
  padding: 0 16px 14px 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const Resumen = styled.p`
  margin: 0;
  color: var(--text-secondary);
  font-size: 0.88rem;
  line-height: 1.55;
`;

const Acciones = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const boton = `
  display: inline-flex;
  align-items: center;
  gap: 7px;
  padding: 7px 13px;
  border-radius: 5px;
  font-size: 0.8rem;
  font-weight: 600;
  cursor: pointer;
  text-decoration: none;
  font-family: inherit;
  transition: filter 0.15s ease;
  &:hover { filter: brightness(1.15); }
  &:disabled { opacity: 0.5; cursor: default; }
`;

const BotonKit = styled(Link)`
  ${boton}
  background: var(--alert-error);
  border: 1px solid var(--alert-error);
  color: #fff;
`;

const BotonSecundario = styled.button`
  ${boton}
  background: transparent;
  border: 1px solid rgba(239, 71, 111, 0.4);
  color: var(--alert-error);
`;

const Meta = styled.span`
  font-family: 'JetBrains Mono', 'Fira Code', Consolas, monospace;
  font-size: 0.68rem;
  color: var(--text-secondary);
  align-self: center;
`;

export default function CrisisBanner() {
  const { crisis, refrescar } = useCrisis();
  const [abierto, setAbierto] = useState(true);
  const [guardando, setGuardando] = useState(false);
  const [fallo, setFallo] = useState(false);

  // Una crisis nueva vuelve a desplegar el banner aunque lo hubieran plegado.
  useEffect(() => {
    if (!crisis) return;
    const visto = sessionStorage.getItem('crisis_plegada');
    setAbierto(visto !== String(crisis.id));
  }, [crisis]);

  const alternar = () => {
    setAbierto((v) => {
      const nuevo = !v;
      try {
        if (nuevo) sessionStorage.removeItem('crisis_plegada');
        else if (crisis) sessionStorage.setItem('crisis_plegada', String(crisis.id));
      } catch { /* modo privado: no es grave */ }
      return nuevo;
    });
  };

  const responder = async (estado) => {
    if (!crisis || guardando) return;
    setGuardando(true);
    setFallo(false);
    try {
      await cambiarEstadoCrisis(crisis.id, estado);
      await refrescar();
    } catch {
      // Es el control de crisis siempre visible: un fallo mudo acá es grave.
      setFallo(true);
    } finally {
      setGuardando(false);
    }
  };

  if (!crisis) return null;

  const sinConfirmar = crisis.estado === 'detectada';

  return (
    <Barra role="alert">
      <Cabecera onClick={alternar} aria-expanded={abierto}>
        <Punto />
        <Etiqueta>
          {sinConfirmar ? 'Situación detectada' : 'Modo crisis'} · {crisis.severidad}
        </Etiqueta>
        <Titulo>{crisis.titulo}</Titulo>
        <Flecha>{abierto ? <FaChevronUp /> : <FaChevronDown />}</Flecha>
      </Cabecera>

      {abierto && (
        <Cuerpo>
          {crisis.resumen && <Resumen>{crisis.resumen}</Resumen>}
          <Acciones>
            {/* Al panel, no al kit: el kit es un texto, el panel es la situación
                completa — cuánto pegó, qué falta hacer y quién lo está haciendo. */}
            <BotonKit to={`/crisis/${crisis.id}`}>
              <FaFolderOpen /> Abrir la sala de situación
            </BotonKit>

            {sinConfirmar ? (
              <>
                <BotonSecundario onClick={() => responder('confirmada')} disabled={guardando}>
                  <FaExclamationTriangle /> Confirmar que es real
                </BotonSecundario>
                <BotonSecundario onClick={() => responder('falsa_alarma')} disabled={guardando}>
                  No era nada
                </BotonSecundario>
              </>
            ) : (
              <BotonSecundario onClick={() => responder('resuelta')} disabled={guardando}>
                Dar por cerrada
              </BotonSecundario>
            )}

            <Meta>
              {crisis.senales} {crisis.senales === 1 ? 'señal' : 'señales'} · desde el{' '}
              {new Date(crisis.first_seen).toLocaleDateString('es-AR')}
            </Meta>
          </Acciones>
          {fallo && (
            <Resumen style={{ color: 'var(--alert-error)', marginTop: 8 }}>
              No se pudo actualizar. Revisá la conexión y probá de nuevo.
            </Resumen>
          )}
        </Cuerpo>
      )}
    </Barra>
  );
}
